import {AfterViewInit, Component, EventEmitter, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {UploadInput, UploadStatus, UploadFile, UploadOutput, humanizeBytes} from 'ngx-uploader';
import {environment} from '../../../../../environments/environment';
import {MediaLibraryService} from '../../../../media-library/shared/media-library.service';
import {AuthService} from '../../../../auth/auth.service';
import {Storyboard} from 'storyboard-renderer';
import {TimelineService} from "../timeline.service";
import {TimelineTimestamp} from "storyboard-renderer";
import {Template} from "../../../../template/shared/template";
import {StoryboardRendererAudioService} from "../../preview/preview-render-component/storyboard-renderer-audio.service";
import {AlertService} from "../../../../shared/alert/alert.service"
import {Alert} from "../../../../shared/alert/Alert"
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'app-audio-track',
  templateUrl: './audio-track.component.html',
  styleUrls: ['./audio-track.component.scss']
})
export class AudioTrackComponent implements OnInit, AfterViewInit {

  @Input() storyboard: Storyboard;

  audioFile: UploadFile;
  uploadInput: EventEmitter<UploadInput>;
  humanizeBytes: Function;
  dragOver: boolean;
  remaining = '';
  total = '';
  speed = '';
  uploadPercent = 0;
  done = UploadStatus.Done;
  duration = {
    minutes: 0,
    seconds: 0
  };
  userId = null;

  @ViewChild('waveformContainer') waveformContainer;

  uploading = false;
  private endpoint = environment.apiBaseUrl + '/api/v1.0/';

  constructor(
    private audioService: StoryboardRendererAudioService,
    public timelineService: TimelineService,
    private mediaLibraryService: MediaLibraryService,
    private authService: AuthService,
    protected alertService: AlertService,
    protected route: ActivatedRoute
  ) {
    this.audioFile = null; // local uploading files array
    this.uploadInput = new EventEmitter<UploadInput>(); // input events, we use this to emit data to ngx-uploader
    this.humanizeBytes = humanizeBytes;

    this.route.params.subscribe(params => {
      this.userId = +params['user_id']; // (+) converts string 'id' to a number
    });
  }

  ngOnInit() {
    this.duration.seconds = this.storyboard.getDuration().seconds;
    this.duration.minutes = this.storyboard.getDuration().minutes;
  }

  ngAfterViewInit() {
    this.calculateTimelineLength();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.calculateTimelineLength();
  }

  private calculateTimelineLength() {
    if (!this.waveformContainer) {
      return;
    }

    this.timelineService.setTimelineWidthInPx(this.waveformContainer.nativeElement.offsetWidth);
    const diff = Math.abs(this.waveformContainer.nativeElement.offsetWidth -
        this.timelineService.convertTimeToPx(this.storyboard.getDuration().as_milliseconds));
    this.timelineService.setScrollEnabled(diff > 0);
  }

  checkFileFormat(file): Boolean {
    if (file.type.indexOf('mpeg') >= 0 || file.type.indexOf('mp3') >= 0 || file.type.indexOf('wav') >= 0) {
      return true;
    }

    return false;
  }

  checkValidInputs() {
    const regEx =  new RegExp('^[0-9]*$');
    if (!regEx.test(this.duration.seconds.toString())) {
      this.duration.seconds = this.storyboard.getDuration().seconds;
    }

    if (this.duration.seconds >= 60) {
      this.duration.seconds = 59;
    }

    if (!regEx.test(this.duration.minutes.toString())) {
      this.duration.minutes = this.storyboard.getDuration().minutes;
    }

    if (this.duration.minutes >= 20) {
      this.duration.minutes = 19;
    }
  }

  onChangeDuration(event) {

    this.checkValidInputs();

    const newDuration = TimelineTimestamp.fromMilliseconds((this.duration.minutes * 60 * 1000) + (this.duration.seconds * 1000));
    this.storyboard.setDuration(newDuration);

    /**
     * refresh the UI with the new duration
     *
     * storyboard.setDuration CAN(!!!) change the duration's value, so it's
     * important to get a fresh duration value here
     */
    this.duration.seconds = this.storyboard.getDuration().seconds;
    this.duration.minutes = this.storyboard.getDuration().minutes;


    this.timelineService.durationChangeEvent.next(this.storyboard.getDuration());

    // Trigger Storyboard Object Change
    this.timelineService.storyboardChangeEvent.next(this.storyboard);
  }

  onUploadOutput(output: UploadOutput): void {
    if (output.type === 'addedToQueue') {
      if (this.checkFileFormat(output.file)) {
        this.audioFile = output.file;

        setTimeout(() => {
          /**
           * sorry emiatt, fogalmam sincs hogy hol kellene indítani a startUpload()-ot, hogy
           * meg is történjen :\
           */
          this.startUpload();
        }, 250);
      } else {
        this.displayInvalidFileFormatWarning();
      }
    } else if (output.type === 'uploading') {
      // update current data in files array for uploading file

      const _total = this.audioFile.size;
      const _remaining = this.audioFile.size * (this.audioFile.progress.data.percentage / 100);

      this.total = this.humanizeBytes(_total);
      this.remaining = this.humanizeBytes(_remaining);
      this.uploadPercent = _remaining / _total * 100;
      this.speed = this.audioFile.progress.data.speedHuman;

    } else if (output.type === 'removed') {
      // remove file from array when removed
      this.audioFile = null;
    } else if (output.type === 'dragOver') { // drag over event
      this.dragOver = true;
    } else if (output.type === 'dragOut') { // drag out event
      this.dragOver = false;
    } else if (output.type === 'drop') { // on drop event
      this.dragOver = false;
    } else if (output.type === 'done') {
      this.uploading = false;
      this.storyboard.attached_audio = output.file.response;
      try {
        this.storyboard.setDuration(TimelineTimestamp.fromMilliseconds(output.file.response.length.as_milliseconds));
      }catch(e){
        console.log("Unable to set storyboard duration from audio upload response", e.message, e);
      }
      this.audioService.setAudioMedia(this.storyboard.attached_audio.media);
      try {
        this.timelineService.getPlayerControl().setAudioService(this.audioService);
      }catch(e){}

      this.duration.seconds = this.storyboard.getDuration().seconds;
      this.duration.minutes = this.storyboard.getDuration().minutes;

      // Trigger Storyboard Object Change
      this.timelineService.storyboardChangeEvent.next(this.storyboard);
    }
  }

  protected displayInvalidFileFormatWarning(){
    if (this.alertService) {
      this.alertService.danger(new Alert('Only MP3 and WAV files are supported.'));
    }
  }

  startUpload(): void {  // manually start uploading

    this.uploading = true;
    this.storyboard.attached_audio = null;

    const userId = this.userId || 'me';
    let uploadAudioUrl = this.endpoint + 'user/' + userId + '/storyboard/' + this.storyboard.id + '/attached_audio';

    if (this.storyboard.constructor.name === Template.name) {
      uploadAudioUrl = this.endpoint + 'user/' + userId + '/storyboard_template/' + this.storyboard.id + '/attached_audio';
    }

    const event: UploadInput = {
      type: 'uploadAll',
      headers: {'Authorization': 'Bearer ' + this.authService.getCurrentUserToken(), 'Accept': 'application/json'},
      url: uploadAudioUrl,
      method: 'POST',
      concurrency: 1 // set sequential uploading of files with concurrency 1
    };

    this.uploadInput.emit(event);
  }

  cancelUpload(id: string): void {
    this.uploadInput.emit({ type: 'cancel', id: id });
  }
}



// WEBPACK FOOTER //
// ./src/app/storyboard/storyboard-edit/timeline/audio-track/audio-track.component.ts